/*
  MASTER — Transaction core.

  Shopify is the only authority on cart state. This file owns every mutation the
  storefront performs, so there is exactly one engine and one confirmed cart.

    confirmed Shopify cart
          -> user intent (desired quantity per line identity)
          -> coalesced + serialized mutation
          -> Shopify response
          -> identity reconciliation
          -> render

  Rapid clicks never let a stale response overwrite a newer intent: intents are
  written into `desired` (last-intent-wins per line) and the flusher drains that
  map, so only one request is ever in flight and the final state is whatever the
  user last asked for.
*/
(() => {
  const drawer = document.querySelector('[data-cart-drawer]');
  const overlay = document.querySelector('[data-cart-overlay]');
  const root = window.MasterTheme?.root || '/';
  const currency = window.MasterTheme?.currency || window.Shopify?.currency?.active;
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  /*
    Se resuelve en cada llamada, no al cargar: master-money.js va con defer igual
    que este archivo y no queremos depender del orden de ejecución.
  */
  const money = cents => window.MasterMoney
    ? window.MasterMoney.format(cents)
    : new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0
      }).format((Number(cents) || 0) / 100);

  const esc = value => String(value ?? '').replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const status = drawer?.querySelector('[data-cart-status]');
  const announce = message => {
    if (!status || !message) return;
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = message; });
  };

  /* ------------------------------------------------------------------ *
   * Scroll lock. `overflow:hidden` on body does not hold on iOS Safari;
   * the position:fixed + restore dance does. Reference counted so the
   * drawer and the rescue modal can both hold the lock.
   * ------------------------------------------------------------------ */
  const scrollLock = (() => {
    let depth = 0;
    let offset = 0;
    return {
      lock(token) {
        if (depth++ > 0) return;
        offset = window.scrollY || window.pageYOffset || 0;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${offset}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        if (token) document.body.classList.add(token);
      },
      unlock(token, options = {}) {
        if (token) document.body.classList.remove(token);
        if (depth === 0 || --depth > 0) return;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        if (options.restoreScroll !== false) window.scrollTo(0, offset);
      }
    };
  })();
  window.MasterScrollLock = scrollLock;

  /* ------------------------------------------------------------------ *
   * Line identity. `line_item.key` is not stable: applying a discount or
   * changing properties can re-key a line. The durable identity is the
   * variant plus everything that makes Shopify treat a line as distinct.
   * ------------------------------------------------------------------ */
  const propertiesSignature = properties => {
    if (!properties || typeof properties !== 'object') return '';
    return Object.keys(properties)
      .filter(name => properties[name] !== null && properties[name] !== '')
      .sort()
      .map(name => `${name}=${properties[name]}`)
      .join(';');
  };

  const signatureOf = item => [
    Number(item?.variant_id || item?.id || 0),
    item?.selling_plan_allocation?.selling_plan?.id || '',
    propertiesSignature(item?.properties)
  ].join('|');

  // Resolve a remembered line to its line key in a fresh cart.
  // Exact key wins. Otherwise the signature must identify exactly one line —
  // an ambiguous match is reported, never guessed.
  const resolveLine = (cart, ref) => {
    const items = cart?.items || [];
    if (ref?.key) {
      const exact = items.find(item => String(item.key) === String(ref.key));
      if (exact) return {item: exact, ambiguous: false};
    }
    if (!ref?.signature) return {item: null, ambiguous: false};
    const matches = items.filter(item => signatureOf(item) === ref.signature);
    if (matches.length === 1) return {item: matches[0], ambiguous: false};
    return {item: null, ambiguous: matches.length > 1};
  };

  /* ------------------------------------------------------------------ *
   * Savings semantics. Three distinct concepts, never summed blindly:
   *
   *   shopify_discount_savings  original_total_price -> total_price.
   *                             Shopify's real transactional discount.
   *   compare_at_savings        compare_at_price -> original price.
   *                             Catalog markdown. Not present in cart.js,
   *                             so it is seeded from Liquid by variant.
   *   bundle_display_savings    merchant-configured display value on an
   *                             offer tier. Never called "applied".
   *
   * The first two do not overlap: compare-at measures down to the line's
   * original price, Shopify's discount measures from there to final.
   * ------------------------------------------------------------------ */
  const compareAtIndex = new Map();
  const seedCompareAt = () => {
    document.querySelectorAll('[data-compare-at-index]').forEach(node => {
      try {
        const data = JSON.parse(node.textContent || '{}');
        Object.keys(data).forEach(variantId => {
          const value = Number(data[variantId]) || 0;
          if (value > 0) compareAtIndex.set(String(variantId), value);
        });
      } catch (_) {}
    });
  };

  const savingsOf = cart => {
    const shopify = Math.max(0, Number(cart?.total_discount || 0));
    let compareAt = 0;
    (cart?.items || []).forEach(item => {
      const reference = compareAtIndex.get(String(item.variant_id || item.id));
      const unit = Number(item.original_price || item.final_price || 0);
      if (reference && reference > unit) compareAt += (reference - unit) * Number(item.quantity || 0);
    });
    return {shopify, compareAt, total: shopify + compareAt};
  };

  /* ------------------------------------------------------------------ *
   * Transport
   * ------------------------------------------------------------------ */
  const shopifyError = async response => {
    let message = 'No se pudo completar la acción.';
    try {
      const data = await response.json();
      message = data.description || data.message || message;
    } catch (_) {}
    return message;
  };

  const postJSON = async (path, body) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(await shopifyError(response));
    return response.json();
  };

  const readCart = async () => {
    const response = await fetch(`${root}cart.js`, {headers: {Accept: 'application/json'}, cache: 'no-store'});
    if (!response.ok) throw new Error('No se pudo leer el carrito.');
    return response.json();
  };

  /* ------------------------------------------------------------------ *
   * Confirmed state + desired state
   * ------------------------------------------------------------------ */
  let confirmed = null;          // last cart Shopify confirmed
  const desired = new Map();     // signature -> {quantity, ref, item, attempts}
  let flushing = null;           // in-flight flush promise
  let busyDepth = 0;

  /* ------------------------------------------------------------------ *
   * Transaction barrier.
   *
   * Coalescing quantity clicks was not enough on its own: quantity, discount,
   * add and clear are different mutation CLASSES, and only the first was
   * serialized. A discount landing between a drain's key resolution and its
   * response rotates line keys, so the update targets a key that no longer
   * exists — and its older response then overwrites the newer confirmed cart.
   *
   * Every network mutation now passes through this single-slot queue.
   * Coalescing still happens in `desired` BEFORE the barrier is acquired, so a
   * burst of +/- clicks is still one request: the barrier orders operations
   * between classes, it does not serialize individual clicks.
   * ------------------------------------------------------------------ */
  let barrier = Promise.resolve();
  const withBarrier = task => {
    const run = barrier.then(task, task);
    // A rejected task must not poison the queue for everything behind it.
    barrier = run.then(() => {}, () => {});
    return run;
  };

  const setBusy = busy => {
    busyDepth = Math.max(0, busyDepth + (busy ? 1 : -1));
    const active = busyDepth > 0;
    drawer?.setAttribute('aria-busy', active ? 'true' : 'false');
    document.documentElement.classList.toggle('master-cart-busy', active);
  };

  const seedConfirmed = () => {
    const node = document.querySelector('[data-cart-seed]');
    if (!node) return;
    try {
      const cart = JSON.parse(node.textContent || 'null');
      if (cart && Array.isArray(cart.items)) confirmed = cart;
    } catch (_) {}
  };

  // Stamp the durable signature onto every server-rendered line while its key
  // is still the one Shopify knows.
  //
  // Server markup ships only `data-line-key`. Deriving the signature lazily on
  // first interaction was too late: applying a discount rotates line keys, so
  // after the first discount the old DOM key no longer existed in the confirmed
  // cart, the signature stayed empty, and the line could not be resolved at all
  // — it was dropped from /cart. Seeding up front closes that window.
  const seedLineSignatures = () => {
    if (!confirmed?.items?.length) return;
    document.querySelectorAll('[data-line-key],[data-cart-page-line]').forEach(node => {
      if (node.dataset.lineSignature) return;
      const match = confirmed.items.find(item => String(item.key) === String(node.dataset.lineKey || ''));
      if (match) node.dataset.lineSignature = signatureOf(match);
    });
  };

  const setConfirmed = cart => {
    if (cart && Array.isArray(cart.items)) confirmed = cart;
    return confirmed;
  };

  const ensureConfirmed = async () => {
    if (confirmed) return confirmed;
    return setConfirmed(await readCart());
  };

  // Current quantity for a line: the pending intent if one exists, otherwise
  // whatever Shopify last confirmed. This is what the +/- buttons step from,
  // so a burst of clicks accumulates instead of fighting the network.
  //
  // CRITICAL: `desired` is NOT cleared while a request is in flight. It used to
  // be, which meant every click during that window fell through to `confirmed`
  // — still holding the pre-request cart — so ten rapid `+` clicks all computed
  // the same base and landed on 10 instead of 11. Entries are retired only
  // after Shopify confirms the exact value that was sent.
  const quantityOf = ref => {
    if (!ref?.signature) return 0;
    if (desired.has(ref.signature)) return desired.get(ref.signature).quantity;
    const {item} = resolveLine(confirmed, ref);
    return Number(item?.quantity || 0);
  };

  // Keep the last known cart item for the signature so a line that Shopify has
  // already removed can be re-added if the user asks for it again.
  const intend = (ref, quantity) => {
    if (!ref?.signature) return;
    const previous = desired.get(ref.signature);
    const {item} = resolveLine(confirmed, ref);
    desired.set(ref.signature, {
      quantity: Math.max(0, Number(quantity) || 0),
      ref,
      item: item || previous?.item || ref.item || null
    });
  };

  // Rebuild an add payload for a line Shopify no longer has. The remembered
  // item is authoritative; the signature carries the variant id as a fallback.
  const addPayloadFor = (intent, signature) => {
    const item = intent.item;
    const variantId = Number(item?.variant_id || item?.id) || Number(String(signature).split('|')[0]) || 0;
    if (!variantId) return null;
    const payload = {id: variantId, quantity: intent.quantity};
    if (item?.properties && Object.keys(item.properties).length) payload.properties = item.properties;
    const sellingPlan = item?.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlan) payload.selling_plan = sellingPlan;
    return payload;
  };

  // Drain the desired map. One request in flight at a time. Each lap resolves
  // every outstanding intent against the freshest confirmed cart, so a delta
  // recorded mid-flight is recomputed rather than lost.
  const drain = () => {
    if (flushing) return flushing;
    setBusy(true);
    flushing = (async () => {
      try {
        let guard = 0;
        while (desired.size && guard++ < 24) {
          // The whole lap — resolve keys, post, reconcile — is one barrier
          // slot. Resolving outside it let another mutation class rotate the
          // keys we had just resolved against.
          const done = await withBarrier(async () => {
            const cart = await ensureConfirmed();
            const batch = [...desired.entries()].map(([signature, intent]) => ({
              signature, intent, sent: intent.quantity
            }));

            const updates = {};
            const adds = [];
            const unresolved = [];

            batch.forEach(entry => {
              const {item, ambiguous} = resolveLine(cart, entry.intent.ref);
              if (item) {
                updates[String(item.key)] = entry.intent.quantity;
                entry.intent.ref.key = String(item.key);
                entry.mode = 'update';
                return;
              }
              if (ambiguous) {
                unresolved.push(entry.signature);
                return;
              }
              // The line is not in the cart. Absence satisfies a desired zero,
              // but a positive intent means the user wants it back — a `−` to 0
              // immediately followed by `+` used to be silently dropped here.
              if (entry.intent.quantity > 0) {
                const payload = addPayloadFor(entry.intent, entry.signature);
                if (payload) {
                  adds.push(payload);
                  entry.mode = 'add';
                  return;
                }
              }
              entry.mode = 'satisfied';
            });

            if (unresolved.length) {
              throw new Error('No pudimos identificar la línea del carrito. Recarga la página.');
            }

            // Retire an intent only when Shopify's confirmed cart ACTUALLY
            // reflects it. Retiring on "the user did not change their mind"
            // was the second half of this bug: when a concurrent discount
            // re-keyed the line, the update became a no-op, yet the intent was
            // dropped as if satisfied. Verifying convergence makes an
            // unapplied intent survive and retry against the fresh cart.
            const retire = modes => batch.forEach(entry => {
              if (!modes.includes(entry.mode)) return;
              const current = desired.get(entry.signature);
              if (!current || current.quantity !== entry.sent) return;
              const {item} = resolveLine(confirmed, current.ref);
              const actual = Number(item?.quantity || 0);
              if (actual === current.quantity) {
                desired.delete(entry.signature);
                return;
              }
              // Not converged. Keep it, but do not spin forever if Shopify
              // simply refuses (inventory cap, line no longer purchasable).
              current.attempts = (current.attempts || 0) + 1;
              if (current.attempts >= 4) {
                desired.delete(entry.signature);
                throw new Error('No pudimos confirmar la cantidad. Inténtalo de nuevo.');
              }
            });

            // Re-adds go in their own lap: they change line keys, so pending
            // updates must be re-resolved against the cart Shopify returns.
            if (adds.length) {
              setConfirmed(await postJSON('cart/add.js', {items: adds}));
              retire(['add']);
              return false;
            }

            if (!Object.keys(updates).length) {
              retire(['satisfied']);
              return !desired.size;
            }

            setConfirmed(await postJSON('cart/update.js', {updates}));
            retire(['update', 'satisfied']);
            return false;
          });
          if (done) break;
        }
        return confirmed;
      } finally {
        flushing = null;
        setBusy(false);
      }
    })();
    return flushing;
  };

  // Joining an in-flight drain is not enough on its own: an intent recorded
  // just as that drain finishes its last lap would otherwise be left behind.
  // Re-check after awaiting and start a fresh drain if anything is still due.
  const flush = async () => {
    let result = confirmed;
    let guard = 0;
    while (desired.size && guard++ < 8) {
      result = await drain();
    }
    return result || confirmed;
  };

  // Public mutation entry point. Optimistic paint first (so the number moves
  // under the finger), then converge on Shopify.
  const setLineQuantity = async (ref, quantity) => {
    intend(ref, quantity);
    paintOptimistic();
    try {
      const cart = await flush();
      commit(cart);
      return cart;
    } catch (error) {
      // Resync on the barrier so the recovery read cannot itself be overtaken.
      const cart = await withBarrier(async () => readCart()).catch(() => null);
      if (cart) commit(setConfirmed(cart));
      throw error;
    }
  };

  const stepLine = (ref, delta) => setLineQuantity(ref, quantityOf(ref) + Number(delta || 0));

  /* ------------------------------------------------------------------ *
   * Discounts.
   *
   * POST /cart/update.js {discount: "CODE"} is the officially supported
   * Cart AJAX mechanism (shopify.dev changelog, 2025-05-21): it accepts
   * multiple comma-separated codes and clears every code on empty string.
   * Shopify decides eligibility and combination — we only read back what it
   * confirms, and never claim a code was applied without that confirmation.
   * ------------------------------------------------------------------ */
  /*
    Shopify devuelve DOS formas distintas para el mismo campo:

      GET  /cart.js         cart_level_discount_applications[i] = la aplicación
                            en sí            -> {type, title, total_allocated_amount}
      POST /cart/update.js  cart_level_discount_applications[i] = una asignación
                            que la envuelve  -> {amount, discount_application:{…}}

    Leer sólo la primera dejaba un descuento de clase PEDIDO como "no aplicado"
    justo después de aplicarlo: el título vivía un nivel más abajo. No se notaba
    con un rescate de clase PRODUCTO porque ese aparece a nivel de línea, donde
    la envoltura sí se deshacía. `unwrap` acepta las dos formas.
  */
  const unwrapApplication = entry => entry?.discount_application || entry;
  const allDiscountApplications = cart => {
    const apps = (cart?.cart_level_discount_applications || []).map(unwrapApplication);
    (cart?.items || []).forEach(item => (item.line_level_discount_allocations || []).forEach(allocation => {
      const app = unwrapApplication(allocation);
      if (app) apps.push(app);
    }));
    return apps.filter(Boolean);
  };

  const activeDiscountCodes = cart => [...new Set(allDiscountApplications(cart)
    .filter(app => ['discount_code', 'code'].includes(String(app?.type || '').toLowerCase()))
    .map(app => String(app?.title || '').trim())
    .filter(Boolean))];

  const hasDiscountCode = (cart, code) => activeDiscountCodes(cart)
    .some(value => value.toLowerCase() === String(code || '').trim().toLowerCase());

  const normalizeCodes = value => [...new Set(
    (Array.isArray(value) ? value : String(value ?? '').split(','))
      .map(code => String(code).trim())
      .filter(Boolean)
  )];

  // Replace the cart's discount codes with exactly this set. Empty clears all.
  //
  // Runs on the shared barrier: applying a code re-keys lines, so it must not
  // interleave with a quantity drain. Any quantity intent still pending is
  // re-converged afterwards against the re-keyed cart.
  const setDiscountCodes = async (value, options = {}) => {
    const codes = normalizeCodes(value);
    let cart = await withBarrier(async () =>
      setConfirmed(await postJSON('cart/update.js', {discount: codes.join(',')})));
    if (desired.size) cart = await flush();
    if (options.render !== false) commit(cart);
    return cart;
  };

  // Add one code, keeping whatever Shopify already accepted.
  const addDiscountCode = async (code, options = {}) => {
    const wanted = String(code || '').trim();
    if (!wanted) return ensureConfirmed();
    const before = await ensureConfirmed();
    return setDiscountCodes([...activeDiscountCodes(before), wanted], options);
  };

  const removeDiscountCode = async (code, options = {}) => {
    const drop = String(code || '').trim().toLowerCase();
    const before = await ensureConfirmed();
    const keep = activeDiscountCodes(before).filter(value => value.toLowerCase() !== drop);
    return setDiscountCodes(keep, options);
  };

  const clearDiscountCodes = (options = {}) => setDiscountCodes([], options);

  // Money attributable to ONE specific code, never the cart-wide total.
  //
  // Returns null when no amount can be safely attributed — the caller must then
  // show no figure at all. Crediting `cart.total_discount` to the requested code
  // over-reports the moment a second code or an automatic discount is active.
  const discountAmountForCode = (cart, code) => {
    const wanted = String(code || '').trim().toLowerCase();
    if (!wanted) return null;
    const matches = app => String(app?.title || '').trim().toLowerCase() === wanted;
    let total = 0;
    let attributable = false;

    // Las dos formas de Shopify otra vez: en la envuelta el importe está en
    // `amount`, en la plana en `total_allocated_amount`. Ver `unwrapApplication`.
    (cart?.cart_level_discount_applications || []).forEach(entry => {
      const app = unwrapApplication(entry);
      if (!matches(app)) return;
      const amount = Number(entry?.discount_application ? entry.amount : app?.total_allocated_amount);
      if (Number.isFinite(amount)) { total += amount; attributable = true; }
    });

    (cart?.items || []).forEach(item => (item?.line_level_discount_allocations || []).forEach(allocation => {
      if (!matches(allocation?.discount_application)) return;
      const amount = Number(allocation?.amount);
      if (Number.isFinite(amount)) { total += amount; attributable = true; }
    }));

    return attributable ? total : null;
  };

  /* ------------------------------------------------------------------ *
   * Discount breakdown.
   *
   * Un solo número —`cart.total_discount`— mezclaba dos cosas que el comprador
   * vive distinto: la promoción por cantidad, que Shopify aplica solo, y el
   * código especial, que él aceptó. Aquí se separan LEYENDO las asignaciones
   * que Shopify confirma; no se recalcula ningún porcentaje.
   *
   * La clasificación NO puede hacerse por `type === 'automatic'`: en la Cart
   * AJAX API el descuento automático llega con `type` en CADENA VACÍA. Sólo el
   * código trae `discount_code`. Así que la regla es la inversa: es código el
   * que lo declara, y automático todo lo demás que traiga importe.
   * ------------------------------------------------------------------ */
  const applicationAmount = (entry, app) => {
    // GET plano -> total_allocated_amount. POST/línea envuelta -> amount.
    const wrapped = entry?.discount_application ? Number(entry.amount) : NaN;
    if (Number.isFinite(wrapped)) return wrapped;
    const flat = Number(app?.total_allocated_amount);
    return Number.isFinite(flat) ? flat : 0;
  };

  const discountBreakdown = (cart, specialCodes = []) => {
    const special = new Set(specialCodes
      .map(code => String(code || '').trim().toLowerCase())
      .filter(Boolean));
    const buckets = {special: 0, automatic: 0, other: 0};

    const consider = entry => {
      const app = unwrapApplication(entry);
      if (!app) return;
      const amount = applicationAmount(entry, app);
      if (!amount) return;
      const isCode = ['discount_code', 'code'].includes(String(app.type || '').toLowerCase());
      if (!isCode) { buckets.automatic += amount; return; }
      const title = String(app.title || '').trim().toLowerCase();
      if (special.has(title)) buckets.special += amount;
      else buckets.other += amount;
    };

    (cart?.cart_level_discount_applications || []).forEach(consider);
    (cart?.items || []).forEach(item =>
      (item?.line_level_discount_allocations || []).forEach(consider));

    // Shopify sigue mandando sobre el total. Si las asignaciones no llegan a
    // explicarlo entero, el resto se declara como "otros" en vez de dejar que
    // las filas sumen menos que el descuento que el cliente está recibiendo.
    const total = Math.max(0, Number(cart?.total_discount || 0));
    const attributed = buckets.special + buckets.automatic + buckets.other;
    if (total > attributed) buckets.other += total - attributed;
    return {...buckets, total};
  };

  /* Los códigos especiales los declara la sección de rescate, no este archivo:
     el theme no debe llevar ningún código de campaña escrito en el JS. Los
     `legacy` existen sólo para poder DESPEGAR códigos de campañas anteriores
     que sigan pegados a un carrito; nunca se ofrecen. */
  const specialDiscountCodes = () => {
    const node = document.querySelector('[data-exit-save]');
    if (!node) return [];
    return [node.dataset.code, ...String(node.dataset.legacyCodes || '').split(',')]
      .map(code => String(code || '').trim())
      .filter(Boolean);
  };

  // Did Shopify actually honour this code?
  //
  // The only sufficient evidence is the code appearing in the cart's discount
  // applications. A price drop is NOT evidence: the cart can get cheaper for
  // reasons that have nothing to do with this code (an automatic discount, a
  // concurrent edit in another tab), and a valid free-shipping code moves no
  // cart total at all. Claiming "applied" from a price delta would be guessing.
  //
  // `savings` is per-code or null. `cartSavings` carries the cart-wide figure
  // separately so the summary can still show it. `delta` is diagnostics only.
  const discountOutcome = (before, after, code) => {
    const applied = hasDiscountCode(after, code);
    return {
      applied,
      savings: applied ? discountAmountForCode(after, code) : null,
      cartSavings: savingsOf(after).shopify,
      delta: Math.max(0, Number(before?.total_price || 0) - Number(after?.total_price || 0))
    };
  };

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */
  const replaceTokens = (template, amount, count) => String(template || '')
    .replaceAll('[amount]', money(Math.max(0, amount)))
    .replaceAll('[count]', String(Math.max(0, count)));

  /*
    Progreso/regalo del carrito. Un único cálculo, parametrizado por `root`
    (drawer o /cart), para que exista una sola fuente de verdad: qué superficie
    pinta cada bloque lo decide el Liquid (sólo emite el marcado si
    `cart_progress_surface` lo permite en esa superficie), no este archivo. Si
    el marcado no existe en `root`, los `querySelector` de abajo devuelven
    `null` y la función no hace nada — no hace falta comprobar la superficie
    aquí también.
  */
  const progress = (cart, root) => {
    if (!root) return;
    const mode = root.dataset.progressMode || 'amount';
    const goalAmount = Number(root.dataset.goal) || 0;
    const goalCount = Number(root.dataset.goalCount) || 0;
    const value = mode === 'quantity' ? Number(cart.item_count || 0) : Number(cart.total_price || 0);
    const goal = mode === 'quantity' ? goalCount : goalAmount;
    const bar = root.querySelector('[data-progress-bar]');
    const text = root.querySelector('[data-progress-text]');

    if (bar && text && goal > 0) {
      bar.style.width = `${Math.min(100, value / goal * 100)}%`;
      const left = Math.max(0, goal - value);
      text.textContent = value >= goal
        ? (root.dataset.progressAfter || 'Beneficio desbloqueado')
        : replaceTokens(
            root.dataset.progressBefore || 'Te falta [amount]',
            mode === 'quantity' ? 0 : left,
            mode === 'quantity' ? left : 0
          );
    }

    const gift = root.querySelector('[data-gift-message]');
    const giftText = root.querySelector('[data-gift-text]');
    if (gift && giftText) {
      const giftGoal = mode === 'quantity'
        ? (Number(root.dataset.giftGoalCount) || 0)
        : (Number(root.dataset.giftGoal) || 0);
      if (giftGoal > 0) {
        const unlocked = value >= giftGoal;
        gift.hidden = cart.item_count === 0;
        gift.classList.toggle('is-unlocked', unlocked);
        const left = Math.max(0, giftGoal - value);
        giftText.textContent = unlocked
          ? (root.dataset.giftUnlocked || 'Desbloqueado')
          : replaceTokens(
              root.dataset.giftLocked || 'Sigue añadiendo productos',
              mode === 'quantity' ? 0 : left,
              mode === 'quantity' ? left : 0
            );
      }
    }
  };

  const lineAttributes = item => [
    `data-line-key="${esc(item.key || '')}"`,
    `data-line-signature="${esc(signatureOf(item))}"`,
    `data-product-id="${Number(item.product_id) || 0}"`,
    `data-variant-id="${Number(item.variant_id || item.id) || 0}"`,
    `data-line-qty="${Number(item.quantity) || 0}"`,
    `data-line-original-unit="${Number(item.original_price || item.final_price || 0)}"`,
    `data-line-final-unit="${Number(item.final_price || 0)}"`,
    // Totales de linea reales: el unitario redondeado por la cantidad no los
    // reconstruye cuando el importe no divide exacto. Ver domCart.
    `data-line-original-total="${Number(item.original_line_price ?? (Number(item.original_price || item.final_price || 0) * Number(item.quantity || 0)))}"`,
    `data-line-final-total="${Number(item.final_line_price ?? (Number(item.final_price || 0) * Number(item.quantity || 0)))}"`
  ].join(' ');

  const lineMarkup = item => {
    const original = Number(item.original_line_price || item.final_line_price || 0);
    const final = Number(item.final_line_price || 0);
    const discounted = original > final;
    const title = esc(item.product_title || '');
    const quantity = Number(item.quantity) || 0;
    return `<article class="master-cart-line" ${lineAttributes(item)}>
      ${item.image ? `<a class="master-cart-line__media" href="${esc(item.url || '#')}" aria-label="Ver ${title}"><img src="${esc(item.image)}" alt="${title}" width="82" height="82" loading="lazy" decoding="async"></a>` : ''}
      <div class="master-cart-line__content">
        <strong class="master-cart-line__title">${title}</strong>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<small class="master-cart-line__variant">${esc(item.variant_title)}</small>` : ''}
        <div class="master-cart-line__prices">${discounted ? `<s>${money(original)}</s>` : ''}<span>${money(final)}</span></div>
        ${discounted ? `<small class="master-cart-line__saving">Descuento ${money(original - final)}</small>` : ''}
        <div class="master-cart-line__actions">
          <div class="master-cart-qty" role="group" aria-label="Cantidad de ${title}">
            <button type="button" data-qty-step data-delta="-1" aria-label="Restar una unidad de ${title}">−</button>
            <span data-line-qty-value aria-live="off">${quantity}</span>
            <button type="button" data-qty-step data-delta="1" aria-label="Agregar una unidad de ${title}">+</button>
          </div>
          <button class="master-cart-remove" type="button" data-line-remove aria-label="Eliminar ${title}">Eliminar</button>
        </div>
      </div>
    </article>`;
  };

  const emptyMarkup = () => `
    <div class="master-cart-empty">
      <strong>Tu carrito está vacío</strong>
      <span>Añade un producto para continuar.</span>
      <button type="button" class="master-cart-empty__cta" data-cart-close data-cart-continue>SEGUIR COMPRANDO</button>
    </div>`;

  const updateUpsell = cart => {
    if (!drawer) return;
    drawer.querySelectorAll('[data-upsell-card]').forEach(card => {
      const productId = Number(card.dataset.upsellProduct) || 0;
      const inCart = productId && cart.items.some(item => Number(item.product_id) === productId);
      card.hidden = card.dataset.upsellHide === 'true' ? Boolean(inCart) : false;
      const btn = card.querySelector('[data-upsell-add]');
      if (!btn) return;
      btn.disabled = Boolean(inCart);
      btn.classList.toggle('is-added', Boolean(inCart));
      if (inCart) {
        btn.dataset.originalLabel = btn.dataset.originalLabel || btn.textContent;
        btn.textContent = 'AÑADIDO ✓';
      } else if (btn.dataset.originalLabel) {
        btn.textContent = btn.dataset.originalLabel;
      }
    });
  };

  // Paint pending intents straight onto the quantity readouts so the UI tracks
  // the finger, not the network. Money stays on confirmed numbers — we never
  // invent a price Shopify has not returned.
  const paintOptimistic = () => {
    document.querySelectorAll('[data-line-key]').forEach(node => {
      const ref = refFromNode(node);
      if (!ref?.signature || !desired.has(ref.signature)) return;
      const quantity = desired.get(ref.signature).quantity;
      const readout = node.querySelector('[data-line-qty-value]');
      if (readout) readout.textContent = String(quantity);
      const input = node.querySelector('[data-cart-page-input]');
      if (input) input.value = String(quantity);
      node.classList.toggle('is-pending-removal', quantity === 0);
    });
  };

  /* Un solo pintor para las dos superficies. Cada fila sale de la asignación
     que Shopify confirma; ninguna cifra se recalcula aquí. Con el carrito
     vacío no se enseña ni una: un descuento sin nada que descontar no es un
     estado que el cliente deba leer. */
  const paintDiscountBreakdown = (scope, cart) => {
    if (!scope) return;
    const parts = discountBreakdown(cart, specialDiscountCodes());
    const empty = Number(cart?.item_count || 0) === 0;

    // En negativo porque restan. La fila «Valor productos» es el punto de
    // partida y el Subtotal el de llegada; entre medias todo baja.
    const paintRow = (name, amount) => {
      const value = scope.querySelector(`[data-cart-${name}-savings]`);
      const row = scope.querySelector(`[data-cart-${name}-savings-row]`);
      if (value) value.textContent = money(amount > 0 ? -amount : 0);
      if (row) row.hidden = empty || amount <= 0;
    };
    paintRow('pack', parts.automatic);
    paintRow('special', parts.special);
    paintRow('other', parts.other);

    // La fila combinada histórica sobrevive para perfiles que aún no muestran
    // el desglose; donde el desglose existe se calla, o el mismo dinero se
    // contaría dos veces.
    const split = Boolean(scope.querySelector('[data-cart-pack-savings-row],[data-cart-special-savings-row]'));
    const legacyValue = scope.querySelector('[data-cart-savings]');
    const legacyRow = scope.querySelector('[data-cart-savings-row]');
    if (legacyValue) legacyValue.textContent = money(parts.total);
    if (legacyRow) legacyRow.hidden = empty || split || parts.total <= 0;

    // El aviso afirma dinero, así que exige dinero: se enciende cuando Shopify
    // ha asignado importe al código especial, no sólo cuando lo lleva pegado.
    const flagged = !empty && parts.special > 0;
    scope.querySelectorAll('[data-cart-special-flag],[data-cart-special-note]')
      .forEach(el => { el.hidden = !flagged; });
    scope.dataset.specialDiscount = flagged ? 'applied' : 'none';
  };

  const renderDrawer = (cart, message = '') => {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.setAttribute('aria-label', `${cart.item_count} artículos`);
    });
    if (!drawer) return;

    drawer.dataset.itemCount = String(Number(cart.item_count || 0));
    drawer.querySelectorAll('[data-drawer-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.setAttribute('aria-label', `${cart.item_count} artículos`);
    });

    const progressWrap = drawer.querySelector('[data-cart-progress]');
    if (progressWrap) progressWrap.hidden = cart.item_count === 0;
    drawer.querySelectorAll('[data-cart-clear]').forEach(btn => { btn.hidden = cart.item_count === 0; });
    drawer.querySelectorAll('[data-native-checkout]').forEach(btn => { btn.disabled = cart.item_count === 0; });

    const saved = savingsOf(cart);
    const total = drawer.querySelector('[data-cart-total]');
    if (total) total.textContent = money(cart.total_price);

    const original = drawer.querySelector('[data-cart-original]');
    if (original) original.textContent = money(cart.original_total_price || cart.total_price);

    const compareValue = drawer.querySelector('[data-cart-compare-savings]');
    const compareRow = drawer.querySelector('[data-cart-compare-savings-row]');
    if (compareValue) compareValue.textContent = money(saved.compareAt);
    if (compareRow) compareRow.hidden = cart.item_count === 0 || saved.compareAt <= 0;

    paintDiscountBreakdown(drawer, cart);

    progress(cart, drawer);
    updateUpsell(cart);

    const wrap = drawer.querySelector('[data-cart-items]');
    if (wrap) wrap.innerHTML = cart.items.length ? cart.items.map(lineMarkup).join('') : emptyMarkup();
    if (message) announce(message);
  };

  /* ------------------------------------------------------------------ *
   * Partial re-render of the /cart section via Shopify's Section Rendering
   * API. Used when the line SET changed (not just quantities), so the markup
   * comes from Liquid instead of being duplicated in JS — and without the
   * full-page reload that would throw away scroll position.
   * ------------------------------------------------------------------ */
  const cartSectionId = () => {
    const wrap = document.querySelector('[data-cart-page-form]')?.closest('[id^="shopify-section-"]');
    return wrap ? wrap.id.replace('shopify-section-', '') : '';
  };

  let refreshingSection = false;
  const refreshCartSection = async () => {
    if (refreshingSection) return false;
    const id = cartSectionId();
    if (!id) return false;
    refreshingSection = true;
    try {
      const response = await fetch(`${root}cart?sections=${encodeURIComponent(id)}`, {
        headers: {Accept: 'application/json'},
        cache: 'no-store'
      });
      if (!response.ok) return false;
      const html = (await response.json())?.[id];
      if (typeof html !== 'string') return false;
      const host = document.getElementById(`shopify-section-${id}`);
      if (!host) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.getElementById(`shopify-section-${id}`) || parsed.body.firstElementChild;
      if (!fresh) return false;
      host.innerHTML = fresh.innerHTML;
      seedCompareAt();
      seedLineSignatures();
      document.dispatchEvent(new CustomEvent('master:cart-section-rendered', {detail: {cart: confirmed}}));
      return true;
    } catch (_) {
      return false;
    } finally {
      refreshingSection = false;
    }
  };

  // The /cart page is server-rendered; reconcile each line in place against the
  // confirmed cart instead of reloading. An emptied cart swaps to the empty
  // state rather than bouncing the browser.
  const renderCartPage = cart => {
    const form = document.querySelector('[data-cart-page-form]');
    if (!form) return;
    const matchedKeys = new Set();

    const shell = form.closest('[data-cart-cro-root]');
    if (shell) shell.dataset.itemCount = String(Number(cart.item_count || 0));

    const emptyState = document.querySelector('[data-cart-page-empty]');
    if (!cart.item_count) {
      form.hidden = true;
      if (emptyState) emptyState.hidden = false;
      return;
    }
    form.hidden = false;
    if (emptyState) emptyState.hidden = true;

    const saved = savingsOf(cart);
    const subtotal = form.querySelector('[data-cart-page-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.total_price);
    const originalTotal = form.querySelector('[data-cart-page-original]');
    if (originalTotal) originalTotal.textContent = money(cart.original_total_price || cart.total_price);

    // Fila combinada histórica de /cart: se mantiene viva para perfiles que no
    // han adoptado el desglose y se calla en cuanto éste existe.
    const splitOnPage = Boolean(form.querySelector('[data-cart-pack-savings-row],[data-cart-special-savings-row]'));
    const discountValue = form.querySelector('[data-cart-page-discount]');
    const discountRow = form.querySelector('[data-cart-page-savings]');
    if (discountValue) discountValue.textContent = money(saved.shopify);
    if (discountRow) discountRow.hidden = splitOnPage || saved.shopify <= 0;

    const compareValue = form.querySelector('[data-cart-page-compare]');
    const compareRow = form.querySelector('[data-cart-page-compare-row]');
    if (compareValue) compareValue.textContent = money(saved.compareAt);
    if (compareRow) compareRow.hidden = saved.compareAt <= 0;

    paintDiscountBreakdown(form, cart);
    progress(cart, form);

    const seen = new Set();
    form.querySelectorAll('[data-cart-page-line]').forEach(line => {
      const {item} = resolveLine(cart, refFromNode(line));
      if (!item || seen.has(String(item.key))) { line.remove(); return; }
      seen.add(String(item.key));
      matchedKeys.add(String(item.key));

      line.dataset.lineKey = String(item.key || '');
      line.dataset.lineSignature = signatureOf(item);
      line.dataset.lineQty = String(Number(item.quantity || 0));
      line.dataset.productId = String(Number(item.product_id || 0));
      line.dataset.variantId = String(Number(item.variant_id || item.id || 0));
      line.dataset.lineOriginalUnit = String(Number(item.original_price || item.final_price || 0));
      line.dataset.lineFinalUnit = String(Number(item.final_price || 0));
      // Igual que en lineAttributes: el total de linea es dato de Shopify, no
      // el producto del unitario redondeado.
      line.dataset.lineOriginalTotal = String(Number(item.original_line_price ?? (Number(item.original_price || item.final_price || 0) * Number(item.quantity || 0))));
      line.dataset.lineFinalTotal = String(Number(item.final_line_price ?? (Number(item.final_price || 0) * Number(item.quantity || 0))));
      line.classList.remove('is-pending-removal');

      const input = line.querySelector('[data-cart-page-input]');
      if (input) {
        input.value = item.quantity;
        input.dataset.confirmedQty = item.quantity;
        input.name = `updates[${String(item.key || '')}]`;
      }
      const readout = line.querySelector('[data-line-qty-value]');
      if (readout) readout.textContent = String(item.quantity);

      const price = line.querySelector('[data-cart-page-line-price]');
      if (price) price.textContent = money(item.final_line_price);
      const compare = line.querySelector('[data-cart-page-line-compare]');
      if (compare) {
        compare.hidden = !(item.original_line_price > item.final_line_price);
        compare.textContent = money(item.original_line_price);
      }
    });

    // Reconciling only the rows that already exist leaves the DOM incomplete
    // when a line was ADDED while the buyer sits on /cart (a drawer upsell, a
    // bundle add): the totals moved but the new product never appeared. Any
    // cart line without a row means the markup has to come from Shopify.
    if (cart.items.some(item => !matchedKeys.has(String(item.key)))) refreshCartSection();
  };

  // Single commit point: every mutation ends here so the drawer, the cart page
  // and every listener see the same confirmed cart at the same time.
  const commit = (cart, message = '') => {
    if (!cart) return cart;
    setConfirmed(cart);
    renderDrawer(cart, message);
    renderCartPage(cart);
    document.dispatchEvent(new CustomEvent('master:cart-updated', {detail: {cart}}));
    publishCompatEvent(cart);
    return cart;
  };

  /* ------------------------------------------------------------------ *
   * Compatibility alias for third-party listeners.
   *
   * This is NOT Shopify's Standard Storefront Events implementation.
   * `cart:updated` is a de-facto convention many apps listen for; the real
   * standard (shopify.dev, 2026-06-17) is the `shopify:cart:lines-update`
   * event class plus overridable `Shopify.actions.updateCart/getCart/openCart`.
   * That migration is documented as pending in ARCHITECTURE_CURRENT.md — it is
   * deliberately NOT faked here, because a half-guessed payload would be worse
   * for an integrating app than no claim at all.
   * ------------------------------------------------------------------ */
  const publishCompatEvent = cart => {
    document.dispatchEvent(new CustomEvent('cart:updated', {detail: {cart}, bubbles: true}));
  };

  // Server-rendered lines only carry a key. Derive the durable signature from
  // the confirmed cart and cache it on the node, so a later re-key (a discount
  // application can re-key a line) still resolves to the right item.
  const refFromNode = node => {
    const line = node?.closest?.('[data-line-key],[data-cart-page-line]') || node;
    if (!line?.dataset) return null;
    let signature = line.dataset.lineSignature || '';
    if (!signature && line.dataset.lineKey) {
      const match = (confirmed?.items || []).find(item => String(item.key) === String(line.dataset.lineKey));
      if (match) {
        signature = signatureOf(match);
        line.dataset.lineSignature = signature;
      }
    }
    return {key: line.dataset.lineKey || '', signature, node: line};
  };

  /* ------------------------------------------------------------------ *
   * Public surface
   * ------------------------------------------------------------------ */
  // Queued behind the barrier so a read that starts mid-mutation cannot land
  // afterwards and overwrite `confirmed` with a pre-mutation cart.
  const getCart = async (options = {}) => {
    const cart = await withBarrier(async () => setConfirmed(await readCart()));
    if (options.render !== false) commit(cart, options.message || '');
    return cart;
  };

  // Emptying is a later, total intent: it discards every pending quantity
  // intent, and clearing INSIDE the barrier slot means a drain lap queued
  // behind it cannot resurrect a line the buyer just removed.
  //
  // Vaciar tiene que soltar TAMBIÉN el código especial. `cart/clear.js` borra
  // líneas, no códigos: el código sigue pegado al carrito y vuelve a morder en
  // cuanto entra el siguiente producto — un carrito "nuevo" de una unidad
  // aparecía a 75.905 en vez de 79.900 sin que nadie hubiera aceptado nada.
  //
  // Y hay que soltarlo AHORA, antes de vaciar: con el carrito ya sin líneas el
  // código es invisible —no hay nada a lo que asignarlo, así que no aparece en
  // ninguna aplicación— y deja de haber forma de saber que estaba ahí.
  //
  // Se retira sólo lo nuestro: cualquier código que haya tecleado el cliente se
  // conserva reescribiendo el conjunto sin el nuestro.
  const clearCart = async () => {
    const cart = await withBarrier(async () => {
      desired.clear();
      const before = await ensureConfirmed();
      const attached = activeDiscountCodes(before);
      const drop = new Set(specialDiscountCodes().map(code => code.toLowerCase()));
      const keep = attached.filter(code => !drop.has(code.toLowerCase()));
      if (keep.length !== attached.length) {
        setConfirmed(await postJSON('cart/update.js', {discount: keep.join(',')}));
      }
      return setConfirmed(await postJSON('cart/clear.js', {}));
    });
    return commit(cart, 'Carrito vaciado.');
  };

  // Un carrito vacío puede seguir arrastrando el código de una vida anterior
  // sin enseñarlo. No se puede leer, pero sí soltar: con cero líneas no hay
  // ningún código del cliente que preservar, así que limpiar es seguro.
  //
  // `hint` viene del rastro local del rescate: sirve para no gastar una
  // petición en cada arranque, nunca como prueba de que exista un descuento.
  const detachSpecialFromEmptyCart = async () => {
    const cart = await ensureConfirmed();
    if (Number(cart?.item_count || 0) > 0) return cart;
    return withBarrier(async () => setConfirmed(await postJSON('cart/update.js', {discount: ''})));
  };

  // Public add path. Everything that puts a line in the cart goes through here
  // so `cart/add.js` has exactly one caller and every add lands in the same
  // commit/render cycle. `items` matches the Cart AJAX shape:
  // [{id, quantity, properties?, selling_plan?}]
  const addItems = async (items, options = {}) => {
    const payload = (Array.isArray(items) ? items : [items])
      .map(entry => {
        const id = Number(entry?.id || entry?.variant_id || 0);
        if (!id) return null;
        const line = {id, quantity: Math.max(1, Number(entry.quantity) || 1)};
        if (entry.properties && Object.keys(entry.properties).length) line.properties = entry.properties;
        if (entry.selling_plan) line.selling_plan = entry.selling_plan;
        return line;
      })
      .filter(Boolean);
    if (!payload.length) throw new Error('No hay productos válidos para añadir.');

    setBusy(true);
    try {
      let cart = await withBarrier(async () => {
        await postJSON('cart/add.js', {items: payload});
        return setConfirmed(await readCart());
      });
      // An add can re-key existing lines; let pending quantity intents settle.
      if (desired.size) cart = await flush();
      if (options.render !== false) commit(cart, options.message || `Producto añadido. ${cart.item_count} artículos en el carrito.`);
      return cart;
    } finally {
      setBusy(false);
    }
  };

  // Set the total quantity of a product across the cart. Used by the offer
  // ladder: the first matching line absorbs the quantity, the rest go to zero.
  const setProductQuantity = async (productId, quantity, options = {}) => {
    const pid = Number(productId) || 0;
    const target = Math.max(0, Number(quantity) || 0);
    const cart = await ensureConfirmed();
    const matches = (cart.items || []).filter(item => !pid || Number(item.product_id) === pid);
    if (!matches.length) throw new Error('No encontramos el producto objetivo en el carrito.');

    matches.forEach((item, index) => {
      intend({key: String(item.key), signature: signatureOf(item)}, index === 0 ? target : 0);
    });

    let result = await flush();
    const reached = (result.items || [])
      .filter(item => !pid || Number(item.product_id) === pid)
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    if (reached !== target) {
      // Verification re-read also goes through the barrier: outside it, a
      // concurrent mutation's response could land after this one and leave
      // `confirmed` describing a cart that no longer exists.
      result = await withBarrier(async () => setConfirmed(await readCart()));
      const retry = (result.items || [])
        .filter(item => !pid || Number(item.product_id) === pid)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      if (retry !== target) throw new Error('No pudimos confirmar la cantidad. Inténtalo de nuevo.');
    }
    if (options.render !== false) commit(result, `Oferta actualizada a ${target} unidad${target === 1 ? '' : 'es'}.`);
    return result;
  };

  window.MasterCart = Object.assign(window.MasterCart || {}, {
    // state
    getCart,
    // mutations
    setLineQuantity,
    stepLine,
    setProductQuantity,
    addItems,
    clearCart,
    refreshCartSection,
    // discounts
    setDiscountCodes,
    addDiscountCode,
    removeDiscountCode,
    clearDiscountCodes,
    activeDiscountCodes,
    hasDiscountCode,
    discountOutcome,
    discountAmountForCode,
    discountBreakdown,
    specialDiscountCodes,
    detachSpecialFromEmptyCart,
    // identity + display
    signatureOf,
    resolveLine,
    seedLineSignatures,
    savingsOf,
    refFromNode,
    render: renderDrawer,
    renderCartPage,
    progress,
    commitCart: commit,
    money,
    // legacy alias kept so older callers keep working through the one engine
    applyDiscount: (code, options) => setDiscountCodes(code, options)
  });

  // Must be defined separately: Object.assign INVOKES a getter on the source and
  // copies the resulting value, so declaring `get confirmed()` in the literal
  // above froze it at its boot-time value of null forever.
  Object.defineProperty(window.MasterCart, 'confirmed', {
    get: () => confirmed,
    enumerable: true,
    configurable: true
  });

  /* ------------------------------------------------------------------ *
   * Drawer open/close
   * ------------------------------------------------------------------ */
  let previousFocus = null;
  const focusableSelector = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const open = () => {
    if (!drawer || drawer.getAttribute('aria-hidden') === 'false') return;
    previousFocus = document.activeElement;
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.hidden = false;
    scrollLock.lock('cart-open');
    requestAnimationFrame(() => {
      (drawer.querySelector('[data-cart-close]') || drawer).focus({preventScroll: true});
    });
  };

  /*
    "SEGUIR COMPRANDO" con el carrito vacío no es un cierre cualquiera: no hay
    ningún "donde estabas antes" útil al que volver —pudo abrirse desde la CTA
    flotante al pie de una página larga— así que en vez de restaurar la
    posición de scroll previa (la "restauración rara" que veía el dueño) se
    manda a un ancla determinista: el buy box. `scrollBuybox` ya es la única
    autoridad sobre ese destino (master-purchase.js); aquí sólo se reutiliza.
  */
  const close = (options = {}) => {
    if (!drawer || drawer.getAttribute('aria-hidden') === 'true') return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    if (overlay) overlay.hidden = true;
    const toBuybox = Boolean(options.redirectToBuybox) && document.querySelector('[data-master-product-form]');
    scrollLock.unlock('cart-open', {restoreScroll: !toBuybox});
    if (toBuybox) {
      window.MasterPurchase?.execute('scroll_buybox');
      return;
    }
    if (previousFocus?.focus) previousFocus.focus({preventScroll: true});
  };

  const requestClose = (trigger, options = {}) => {
    const event = new CustomEvent('master:cart-close-request', {cancelable: true, detail: {trigger}});
    if (!document.dispatchEvent(event)) return false;
    close(options);
    return true;
  };

  document.addEventListener('master:cart-force-close', close);

  document.addEventListener('keydown', event => {
    if (!drawer || drawer.getAttribute('aria-hidden') !== 'false') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      requestClose('escape');
      return;
    }
    if (event.key !== 'Tab') return;
    const focusables = [...drawer.querySelectorAll(focusableSelector)]
      .filter(el => !el.hidden && el.getClientRects().length);
    if (!focusables.length) {
      event.preventDefault();
      drawer.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------------------------ *
   * One delegated click handler for the whole cart surface.
   * ------------------------------------------------------------------ */
  const reportLineError = error => {
    console.error(error);
    announce(error?.message || 'No se pudo actualizar el carrito.');
  };

  document.addEventListener('click', event => {
    const target = event.target;

    const step = target.closest?.('[data-qty-step],[data-cart-page-step]');
    if (step) {
      event.preventDefault();
      const ref = refFromNode(step);
      if (ref) stepLine(ref, Number(step.dataset.delta || 0)).catch(reportLineError);
      return;
    }

    const removeLine = target.closest?.('[data-line-remove],[data-cart-page-remove]');
    if (removeLine) {
      event.preventDefault();
      const ref = refFromNode(removeLine);
      if (ref) setLineQuantity(ref, 0).catch(reportLineError);
      return;
    }

    // Legacy hook: markup that still carries an absolute target quantity.
    const legacy = target.closest?.('[data-change-key]');
    if (legacy) {
      event.preventDefault();
      const ref = refFromNode(legacy) || {key: legacy.dataset.changeKey, signature: ''};
      setLineQuantity(ref, Number(legacy.dataset.qty || 0)).catch(reportLineError);
      return;
    }

    const opener = target.closest?.('[data-cart-open]');
    if (opener) {
      event.preventDefault();
      if (!drawer) {
        location.href = `${root}cart`;
        return;
      }
      open();
      getCart().catch(error => announce(error.message));
      return;
    }

    // SEGUIR COMPRANDO cierra igual que la X, pero no es la misma intención:
    // uno abandona el carrito y el otro vuelve a mirar producto. Se emiten con
    // disparadores distintos para que el rescate pueda interrumpir el primero
    // sin ponerse en medio del segundo.
    const closer = target.closest?.('[data-cart-close]');
    if (closer) {
      const isContinue = closer.matches('[data-cart-continue]');
      const empty = Number(drawer?.dataset.itemCount || 0) === 0;
      requestClose(isContinue ? 'continue' : 'button', {redirectToBuybox: isContinue && empty});
      return;
    }
    if (target.matches?.('[data-cart-overlay]')) {
      requestClose('overlay');
      return;
    }

    const clear = target.closest?.('[data-cart-clear]');
    if (clear) {
      event.preventDefault();
      clear.disabled = true;
      clearCart()
        .catch(reportLineError)
        .finally(() => { clear.disabled = false; });
      return;
    }

    const upsell = target.closest?.('[data-upsell-add]');
    if (upsell) {
      event.preventDefault();
      upsell.disabled = true;
      upsell.dataset.originalLabel = upsell.dataset.originalLabel || upsell.textContent;
      upsell.textContent = 'AÑADIENDO…';
      (async () => {
        try {
          const cart = await addItems([{
            id: Number(upsell.dataset.upsellAdd),
            quantity: Number(upsell.dataset.upsellQty) || 1
          }]);
          announce(`Producto adicional añadido. ${cart.item_count} artículos en el carrito.`);
        } catch (error) {
          reportLineError(error);
          upsell.textContent = upsell.dataset.originalLabel;
        } finally {
          if (!upsell.classList.contains('is-added')) upsell.disabled = false;
        }
      })();
      return;
    }

    const apply = target.closest?.('[data-apply-discount]');
    if (apply) {
      event.preventDefault();
      const scope = apply.closest('[data-cart-discount]') || drawer;
      const input = scope?.querySelector('[data-discount-code]');
      const feedback = scope?.querySelector('[data-discount-status]');
      const requested = input?.value.trim() || '';
      apply.disabled = true;
      setBusy(true);
      (async () => {
        try {
          const before = await ensureConfirmed();
          if (!requested) {
            const cart = await clearDiscountCodes();
            if (feedback) {
              feedback.textContent = activeDiscountCodes(cart).length
                ? 'Tu descuento automático sigue aplicado.'
                : 'Códigos retirados del carrito.';
            }
            return;
          }
          const after = await addDiscountCode(requested);
          const outcome = discountOutcome(before, after, requested);
          if (feedback) {
            // Only name a figure when it is attributable to THIS code. The
            // cart-wide saving stays in the summary row, where it belongs.
            if (!outcome.applied) {
              feedback.textContent = 'No pudimos aplicar ese código promocional.';
            } else if (outcome.savings === null) {
              feedback.textContent = `✓ ${requested} aplicado.`;
            } else {
              feedback.textContent = `✓ ${requested} aplicado. Descuento: ${money(outcome.savings)}.`;
            }
          }
          announce(outcome.applied ? `Código ${requested} aplicado.` : 'El código no se aplicó.');
        } catch (error) {
          if (feedback) feedback.textContent = error?.message || 'No se pudo aplicar el código.';
          announce(error?.message || 'No se pudo aplicar el código.');
        } finally {
          apply.disabled = false;
          setBusy(false);
        }
      })();
    }
  });

  /* ------------------------------------------------------------------ *
   * Manual quantity input on /cart
   * ------------------------------------------------------------------ */
  document.addEventListener('change', event => {
    const input = event.target.closest?.('[data-cart-page-input]');
    if (!input) return;
    const form = input.closest('[data-cart-page-form]');
    if (!form || form.dataset.autoUpdate !== 'true') return;
    const ref = refFromNode(input);
    if (!ref) return;
    setLineQuantity(ref, Math.max(0, Number(input.value) || 0)).catch(error => {
      input.value = input.dataset.confirmedQty || '1';
      reportLineError(error);
    });
  });

  /* ------------------------------------------------------------------ *
   * Add to cart
   * ------------------------------------------------------------------ */
  document.addEventListener('submit', async event => {
    const cartPage = event.target.closest('[data-cart-page-form]');
    if (cartPage && event.submitter?.matches('[data-cart-page-update]')) {
      event.preventDefault();
      event.submitter.disabled = true;
      try {
        const updates = {};
        cartPage.querySelectorAll('[data-cart-page-input]').forEach(input => {
          const key = input.closest('[data-cart-page-line]')?.dataset.lineKey;
          if (key) updates[key] = Math.max(0, Number(input.value) || 0);
        });
        commit(setConfirmed(await postJSON('cart/update.js', {updates})), 'Carrito actualizado.');
      } catch (error) {
        reportLineError(error);
      } finally {
        event.submitter.disabled = false;
      }
      return;
    }

    const form = event.target.closest('[data-master-product-form]');
    if (!form) return;
    event.preventDefault();

    // Un segundo submit sobre el MISMO formulario mientras el primero sigue en
    // vuelo (doble click, doble Enter, o requestSubmit() de MasterPurchase
    // solapado con un submit nativo) no debe abrir un segundo /cart/add: se
    // rechaza en el acto, antes de tocar la red.
    if (form.dataset.submitting === 'true') return;
    form.dataset.submitting = 'true';

    const btn = form.querySelector('[data-atc]');
    const err = form.querySelector('[data-cart-error]');
    if (err) {
      err.hidden = true;
      err.textContent = '';
    }
    btn?.setAttribute('aria-busy', 'true');
    setBusy(true);

    try {
      const discount = (form.querySelector('[data-pack-discount]')?.value || '').trim();
      const bonus = Number(form.querySelector('[data-pack-bonus]')?.value || 0);

      if (bonus) {
        const variantId = Number(form.querySelector('[data-variant-id]')?.value || 0);
        const quantity = Number(form.querySelector('[data-quantity-input]')?.value || 1);
        const offer = form.querySelector('[data-offer-property]')?.value || '';
        const mainItem = {id: variantId, quantity};
        if (form.dataset.saveOfferProperty === 'true' && offer) mainItem.properties = {'_Oferta': offer};
        await postJSON('cart/add.js', {
          items: [mainItem, {id: bonus, quantity: 1, properties: {'_Extra de oferta': 'Sí'}}]
        });
      } else {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: new FormData(form)
        });
        if (!response.ok) throw new Error(await shopifyError(response));
        await response.json();
      }

      let cart = await getCart({render: false});
      if (discount) {
        const before = cart;
        cart = await addDiscountCode(discount, {render: false});
        if (!discountOutcome(before, cart, discount).applied) {
          console.warn(`Shopify no aplicó el código de oferta "${discount}" a este carrito.`);
        }
      }

      // La tarjeta mostró una PROYECCIÓN: antes de añadir no hay carrito que
      // Shopify pueda cotizar. Aquí ya existe, así que se comprueba si la
      // ventaja prometida llegó de verdad. El theme no puede arreglarlo —el
      // descuento vive en Shopify— pero callarlo sería justo la clase de mentira
      // silenciosa que este contrato existe para evitar.
      const card = form.querySelector('[data-pack-card] input:checked')?.closest('[data-pack-card]');
      const promised = Number(card?.dataset.packAdvantage || 0);
      if (promised > 0 && window.MasterOffer) {
        const productId = Number(cart.items?.[0]?.product_id || 0);
        const outcome = window.MasterOffer.verify({advantage: promised}, cart, productId);
        if (outcome && !outcome.honoured) {
          console.warn(
            `[MASTER] El nivel anunciaba ${promised} de ventaja y Shopify aplicó ${outcome.actualAdvantage}. ` +
            'Revisa la promoción por cantidad en Shopify → Descuentos, o pon «Fuente de la ventaja» en «Sin ventaja».'
          );
        }
      }
      commit(cart);
      announce(`Producto añadido. ${cart.item_count} artículos en el carrito.`);

      // Una sola autoridad: MASTER — Conversión escribe `afterAdd` sobre el
      // drawer. `afterAddLegacy` es el antiguo ajuste global, retirado del
      // editor, y sólo se lee cuando esa sección no existe en la tienda.
      //
      // `afterAddOverride` es de un solo uso y lo pone MasterPurchase para la
      // CTA de prepago: ese botón tiene que acabar en el checkout con el nivel
      // elegido, y la forma de garantizarlo sin duplicar la lógica de packs,
      // bonus y código de nivel es reutilizar ESTE envío y cambiarle el final.
      // Se consume aquí mismo para que no contamine el siguiente "añadir".
      const override = form.dataset.afterAddOverride || '';
      delete form.dataset.afterAddOverride;
      const behavior = override || drawer?.dataset.afterAdd || drawer?.dataset.afterAddLegacy || 'drawer';
      if (behavior === 'checkout') {
        location.href = `${root}checkout`;
        return;
      }
      if (behavior === 'cart') {
        location.href = `${root}cart`;
        return;
      }
      if (behavior === 'drawer') {
        open();
      } else if (behavior === 'stay' && btn) {
        const old = btn.textContent;
        btn.textContent = 'AÑADIDO ✓';
        setTimeout(() => { btn.textContent = old; }, 1200);
      }
      return cart;
    } catch (error) {
      const message = error?.message || 'No se pudo añadir al carrito.';
      if (err) {
        err.textContent = message;
        err.hidden = false;
        err.scrollIntoView({behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest'});
      } else {
        console.error(message);
      }
      announce(message);
    } finally {
      btn?.removeAttribute('aria-busy');
      setBusy(false);
      delete form.dataset.submitting;
    }
  });

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  seedCompareAt();
  seedConfirmed();
  seedLineSignatures();
  /* El desglose se pinta ya en el primer frame desde el carrito sembrado por
     Shopify. Liquid no puede clasificarlo por su cuenta —el código especial lo
     declara otra sección, y no vamos a duplicar la regla en dos lenguajes— así
     que el clasificador sigue siendo uno solo y aquí sólo se le pide que pinte
     lo que el servidor ya confirmó. No re-renderiza líneas. */
  if (confirmed) {
    paintDiscountBreakdown(drawer, confirmed);
    paintDiscountBreakdown(document.querySelector('[data-cart-page-form]'), confirmed);
    /* Mismo primer frame que el desglose: sin esto, un cliente que vuelve con
       el carrito ya lleno ve la barra de progreso en 0% y el texto con el
       token [amount] sin reemplazar hasta su primera mutación. */
    progress(confirmed, drawer);
    progress(confirmed, document.querySelector('[data-cart-page-form]'));
  }
  document.addEventListener('shopify:section:load', () => {
    seedCompareAt();
    seedLineSignatures();
    if (confirmed) renderCartPage(confirmed);
  });
})();
